import { DEFAULT_AGENT_NAME } from '@/constants';
import { createContext, useCallback, useContext, useState } from 'react';
import type { AppConfig, UserSettings } from './types';

const defaultConfig: AppConfig = {
  title: 'Enluma Health Navigation',
  description: 'A virtual health navigation assistant.',
  videoFit: 'contain',
  settings: {
    editable: true,
    themeColor: '#42a03f',
    chat: true,
    inputs: {
      camera: false,
      screen: false,
      mic: true,
    },
    outputs: {
      audio: true,
      video: false,
    },
    wsUrl: '',
    token: '',
    roomName: '',
    participantId: '',
    participantName: '',
    metadata: '',
    attributes: [],
    sessionToken: '',
    patientHistories: [],
    agentName: DEFAULT_AGENT_NAME,
  },
  showQr: false,
};

type ConfigContextType = {
  config: AppConfig;
  setUserSettings: (settings: UserSettings) => void;
};

export const ConfigContext = createContext<ConfigContextType | undefined>(
  undefined,
);

export function ConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<AppConfig>(defaultConfig);

  const setUserSettings = useCallback((settings: UserSettings) => {
    setConfig(prev => ({
      ...prev,
      settings,
    }));
  }, []);

  return (
    <ConfigContext.Provider value={{ config, setUserSettings }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

export type { AppConfig, AttributeItem, UserSettings } from './types';
